// src/pages/archived/ArchivedProjectsPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RotateCcw, Trash2, Archive, Loader2, Eye, Filter } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { projectsAPI } from '@/services/projects';
import { cn } from '@/lib/utils';

const ArchivedProjectsPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // ✅ Filter State
  const [selectedStatus, setSelectedStatus] = useState('All');

  // ✅ Status Options (from your image)
  const statusOptions = [
    'All',
    'New Inquiry',
    'Site Visit Scheduled',
    'Quotation Sent',
    'Approved/Awarded',
    'Rejected/Lost'
  ];

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  useEffect(() => {
    fetchArchived();
  }, []);

  const fetchArchived = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.archived();
      if (response.success) {
        setArchived(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load archived projects:', error);
      addNotification('Error', 'Failed to load archived projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id, projectName) => {
    if (!window.confirm(`Restore "${projectName}" to active projects?`)) return;

    try {
      setActionLoading(id);
      const response = await projectsAPI.restore(id);
      
      if (response.success) {
        setArchived(prev => prev.filter(p => p.id !== id));
        addNotification('Success', `"${projectName}" has been restored`, 'success');
      }
    } catch (error) {
      console.error('Failed to restore project:', error);
      addNotification('Error', error.message || 'Failed to restore project', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, projectName) => {
    if (!window.confirm(`Permanently delete "${projectName}"? This action cannot be undone!`)) return;

    try {
      setActionLoading(id);
      const response = await projectsAPI.delete(id);
      
      if (response.success) {
        setArchived(prev => prev.filter(p => p.id !== id));
        addNotification('Warning', `"${projectName}" has been permanently deleted`, 'warning');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      addNotification('Error', error.message || 'Failed to delete project', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'New Inquiry': 'bg-blue-100 text-blue-800 border-blue-200',
      'Site Visit Scheduled': 'bg-orange-100 text-orange-800 border-orange-200',
      'Quotation Sent': 'bg-purple-100 text-purple-800 border-purple-200',
      'Approved/Awarded': 'bg-green-100 text-green-800 border-green-200',
      'Rejected/Lost': 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <Badge className={cn('border', statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200')}>
        {status}
      </Badge>
    );
  };

  // ✅ Filtered Projects
  const filteredProjects = selectedStatus === 'All' 
    ? archived 
    : archived.filter(p => p.status === selectedStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Archived Projects</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Archive className="w-8 h-8 text-gray-600" />
            <div>
              <h1 className="text-3xl font-bold">Archived Projects</h1>
              <p className="text-gray-600 mt-1">
                {filteredProjects.length} of {archived.length} {archived.length === 1 ? 'project' : 'projects'}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/projects')}>
            View Active Projects
          </Button>
        </div>

        {/* ✅ Filter Section */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Filter className="w-4 h-4" />
                Filter by Status:
              </div>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[250px] bg-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(status => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStatus !== 'All' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedStatus('All')}
                  className="text-xs"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Archived Projects List</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">
                  {selectedStatus === 'All' 
                    ? 'No archived projects' 
                    : `No archived projects with status "${selectedStatus}"`
                  }
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedStatus === 'All' 
                    ? 'Archived projects will appear here' 
                    : 'Try selecting a different status'
                  }
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[80px]">#</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Archived Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjects.map((project) => (
                      <TableRow key={project.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          #{project.serial_number}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{project.project_name}</div>
                          <div className="text-xs text-gray-500">{project.work_type}</div>
                        </TableCell>
                        <TableCell>{project.company_name}</TableCell>
                        <TableCell>{getStatusBadge(project.status)}</TableCell>
                        <TableCell>
                          {formatDate(project.archived_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/projects/${project.id}`)}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-gray-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRestore(project.id, project.project_name)}
                              disabled={actionLoading === project.id}
                              title="Restore Project"
                            >
                              {actionLoading === project.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              ) : (
                                <RotateCcw className="w-4 h-4 text-blue-600" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(project.id, project.project_name)}
                              disabled={actionLoading === project.id}
                              title="Delete Permanently"
                            >
                              {actionLoading === project.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-red-600" />
                              ) : (
                                <Trash2 className="w-4 h-4 text-red-600" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {archived.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Archive className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-medium mb-1">About Archived Projects</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-800">
                    <li>Archived projects are hidden from the main projects list</li>
                    <li>You can restore projects at any time</li>
                    <li>Permanently deleted projects cannot be recovered</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default ArchivedProjectsPage;
